import React, { Fragment } from "react";
import {
  RiMoneyDollarBoxLine,
  RiPriceTag3Line,
  RiFlashlightLine,
  RiFileList3Line,
  RiTimerFlashLine
} from "@hacknug/react-icons/ri";
import Padder from "components/Padder";
import { useFormikContext } from "formik";
import { FORM_KEY } from "utils/constants/form";
import { SHORT_LINK_FORM_TYPE } from "utils/constants/shortLink";
import { cellGrow } from "constants/styles";
import { styles } from "components/HeroSection/styles";
import classnames from "classnames";
import Pill from "components/Pill";
import { STATE_KEYS } from "utils/constants/state";
import { getFlexTemplatePrice } from "utils/constants/price";
import {
  AUTHORIZE_PAYMENT_COPY,
  CAPTURE_METHOD,
  PAYMENT_MODE
} from "utils/constants/payment";
import { scrollWindowTop } from "utils/scroll";

const CONFIG = [
  {
    id: SHORT_LINK_FORM_TYPE.PRODUCTS,
    Icon: RiPriceTag3Line,
    label: "Products",
    description:
      "You have a product or subscription that you want to charge for."
  },
  {
    id: SHORT_LINK_FORM_TYPE.PRICE,
    Icon: RiFlashlightLine,
    label: "Quick link",
    description: "You need to charge a single amount."
  },
  {
    id: SHORT_LINK_FORM_TYPE.AUTHORIZE,
    Icon: RiTimerFlashLine,
    label: "Authorize payment",
    description:
      "You need your customer to authorize a payment now but capture it later.",
    footer: AUTHORIZE_PAYMENT_COPY.FOOTER
  },
  {
    id: SHORT_LINK_FORM_TYPE.SETUP,
    Icon: RiFileList3Line,
    label: "Payment details",
    description:
      "You need to collect credit card or debit details from your customer."
  },
  {
    id: SHORT_LINK_FORM_TYPE.PRICES,
    Icon: RiMoneyDollarBoxLine,
    label: "Prices",
    description: "You need to charge one or more amounts."
  }
];

const ShortLinkFormTypePicker = () => {
  const { setFieldValue, setFieldTouched } = useFormikContext();

  return (
    <Fragment>
      <div className="pt3 pt4-l pb4">
        <h1 className="fw6 f3 f2-l pa0 ma0 pb3 pt2">Build a payment link</h1>
        <h3 className="f5 f4-l pt0 fw5 ma0 pa0 lh-copy">
          Choose the type of payment link best suited to your use case.
        </h3>
      </div>
      <div className="grid-container w-100">
        {CONFIG.map(
          ({
            id,
            label,
            description,
            footer,
            status,
            callout,
            Icon,
            disabled
          }) => {
            return (
              <div
                key={id}
                onClick={() => {
                  if (id === SHORT_LINK_FORM_TYPE.PRICE) {
                    setFieldValue(STATE_KEYS.SHORT_LINK.PRICES, [
                      getFlexTemplatePrice()
                    ]);
                    setFieldValue(STATE_KEYS.SHORT_LINK.TRIAL_PERIOD_DAYS, 0);
                  } else if (id === SHORT_LINK_FORM_TYPE.SETUP) {
                    setFieldValue(
                      STATE_KEYS.SHORT_LINK.MODE,
                      PAYMENT_MODE.SETUP
                    );
                  } else if (id === SHORT_LINK_FORM_TYPE.AUTHORIZE) {
                    setFieldValue(
                      STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_CAPTURE_METHOD,
                      CAPTURE_METHOD.MANUAL
                    );
                  }
                  setTimeout(() => {
                    setFieldValue(`${FORM_KEY}.type`, id);
                    setFieldTouched(`${FORM_KEY}.type`, true);
                    scrollWindowTop();
                  }, 0);
                }}
                className={classnames(
                  `${id} flex flex-column w-100 ba br2 hairline-1 pa3 mb3 mb-ns relative justify-between`,
                  {
                    "pointer cell-grow bg-white--hover ba--primary--hover": !disabled,
                    "o-90 cursor-not-allowed": disabled
                  }
                )}
              >
                <div className="flex flex-column">
                  <div className="flex flex-row items-center">
                    <Icon size={24} />
                    <div
                      className="f3 fw6 pl2 color-primary lh-title"
                      style={{ marginTop: 2 }}
                    >
                      {label}
                    </div>
                  </div>
                  <div className="f5 lh-copy pt2 mt1">{description}</div>
                  {footer && <div className="f6 lh-copy pt2 mt1">{footer}</div>}
                </div>
                {callout && (
                  <div className="pt2">
                    <Pill
                      status={status}
                      copy={callout}
                      customClasses={{
                        container: "flex w-100 items-end justify-end"
                      }}
                    />
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
      <Padder theme="visible" />
      <style jsx>{`
        @media (max-width: 30em) {
          .grid-container {
            display: flex;
            flex-direction: column;
          }
        }
        @media (min-width: 30em) {
          .grid-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-template-rows: repeat(1, 1fr);
            gap: 16px 16px;
          }
        }
      `}</style>
      <style jsx>{cellGrow}</style>
      <style jsx>{styles}</style>
    </Fragment>
  );
};

export default ShortLinkFormTypePicker;
